import { ChangeEvent, forwardRef, useEffect, useState } from 'react'
import { useTheme } from '@emotion/react'
import { useTranslation } from 'react-i18next'
import { ConsumerProfile } from '@/libs/helpers/apiClient'
import { formatPhone } from '@/libs/helpers/formatters'
import { validatePhoneNumber } from '@/libs/helpers/validators'
import { CheckBox, Grid, Text, TextField } from '@/tbui'
import { formStyles } from '../tbui/Input/styles'

interface ContactInfoProps {
  onChange: (hasSMS: boolean, phoneNumber: string, emailConsent: boolean) => void
  isValidPhoneNumber?: boolean
  profile: ConsumerProfile | null
  sendSMS: boolean
  phoneNumber: string
  marketingEmailConsent: boolean
  showMarketingEmailConsent: boolean
  marketingGroupName: string
}

// eslint-disable-next-line react/display-name
const ContactInfo = forwardRef<HTMLDivElement, ContactInfoProps>(
  (
    {
      onChange,
      isValidPhoneNumber = true,
      profile,
      phoneNumber,
      sendSMS,
      marketingEmailConsent,
      showMarketingEmailConsent: showMarketingConsent,
      marketingGroupName,
    },
    ref
  ) => {
    const [isValidPhoneNum, setIsValidPhoneNum] = useState<boolean>(isValidPhoneNumber)
    const { t } = useTranslation()
    const theme = useTheme()

    const handleSMSChange = (e: ChangeEvent<HTMLInputElement>): void => {
      onChange(e.target.checked, phoneNumber, marketingEmailConsent)
    }

    const handlePhoneNumChange = (e: ChangeEvent<HTMLInputElement>): void => {
      const formattedPhone = formatPhone(e.target.value)
      onChange(sendSMS, formattedPhone, marketingEmailConsent)
    }

    const handleMarketingEmailConsentChange = (e: ChangeEvent<HTMLInputElement>): void => {
      onChange(sendSMS, phoneNumber, e.target.checked)
    }

    const handleBlur = (): void => {
      // trim all whitespace
      const phoneNum = phoneNumber.replace(/\s+/g, '')
      const isValidNum = validatePhoneNumber(phoneNum)
      setIsValidPhoneNum(isValidNum)
    }

    useEffect(() => {
      setIsValidPhoneNum(isValidPhoneNumber)
    }, [isValidPhoneNumber])

    return (
      <Grid data-test="contact-info" ref={ref} container gap=".75rem">
        <Text type="H3" m="0 0 16px 0">
          {t('checkout.contact.title')}
        </Text>
        {!!profile && (
          <Grid container gap="5px">
            <Text color="TEXT">
              {profile.firstName} {profile.lastName}
            </Text>
            <Text color="TEXT">{profile.email}</Text>
          </Grid>
        )}
        <label
          htmlFor="contact-phone"
          css={[formStyles(theme).label, !isValidPhoneNum && formStyles(theme).invalid]}
        >
          {t('checkout.contact.input_label')}
          <TextField
            variant="INPUT"
            onFocus={(): void => setIsValidPhoneNum(true)}
            onChange={handlePhoneNumChange}
            data-test="contact-phone"
            id="contact-phone"
            name="contact-phone"
            value={phoneNumber}
            onBlur={handleBlur}
            css={[formStyles(theme).input]}
            data-dd-privacy="mask"
            placeholder={t('guest_checkout.phone_number_placeholder')}
          />
          {!isValidPhoneNum && (
            <Text color="ERROR" m="16px 0 0 0">
              {t('checkout.contact.invalid_phone_number')}
            </Text>
          )}
        </label>
        <Grid template="auto 1fr" gap="16px" align="center" m="0">
          <CheckBox
            data-test="sms-notification"
            onChange={handleSMSChange}
            id="sms-notification"
            variant="SQUARE"
            isSelected={sendSMS}
          />
          <Grid gap="6px">
            <Text color="TEXT_2">{t('checkout.contact.sms_notification')}</Text>
          </Grid>
          {showMarketingConsent && (
            <>
              <CheckBox
                data-test="email-consent"
                onChange={handleMarketingEmailConsentChange}
                id="email-consent"
                variant="SQUARE"
                isSelected={marketingEmailConsent}
              />
              <Grid gap="6px">
                <Text color="TEXT_2">
                  {t('checkout.contact.email_consent', { group_name: marketingGroupName })}
                </Text>
              </Grid>
            </>
          )}
        </Grid>
      </Grid>
    )
  }
)

export default ContactInfo
